import { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  useEffect(() => {
    console.log("AuthContext: Setting up auth listener...");
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("AuthContext: Auth state changed", firebaseUser ? "User found" : "No user");
      
      if (firebaseUser) {
        try {
          // Get user document from Firestore
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log("AuthContext: User data loaded", userData);
            
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              ...userData,
            });

            // Get tenant data
            if (userData.tenantId) {
              const tenantDoc = await getDoc(
                doc(db, "tenants", userData.tenantId)
              );
              if (tenantDoc.exists()) {
                setTenant({
                  id: tenantDoc.id,
                  ...tenantDoc.data(),
                });
                console.log("AuthContext: Tenant loaded", tenantDoc.data());
              }
            }

            // Check if system admin
            const adminDoc = await getDoc(
              doc(db, "systemAdmins", firebaseUser.uid)
            );
            setIsSystemAdmin(adminDoc.exists());
            console.log("AuthContext: Is system admin?", adminDoc.exists());
          } else {
            // User exists in Auth but not in Firestore
            // This can happen if user registered but Firestore document wasn't created
            console.warn("AuthContext: User not found in Firestore, setting basic user");
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || "User",
              role: "member"
            });
            setTenant(null);
            setIsSystemAdmin(false);
          }
        } catch (error) {
          console.error("AuthContext: Error fetching user data:", error);
          // Still set basic user info even if Firestore fetch fails
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
          });
          setTenant(null);
          setIsSystemAdmin(false);
        }
      } else {
        // No user logged in
        console.log("AuthContext: No user logged in");
        setUser(null);
        setTenant(null);
        setIsSystemAdmin(false);
      }
      
      setLoading(false);
    });

    return () => {
      console.log("AuthContext: Cleaning up auth listener");
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    console.log("AuthContext: Logging out...");
    try {
      await signOut(auth);
      setUser(null);
      setTenant(null);
      setIsSystemAdmin(false);
      console.log("AuthContext: Logout successful");
    } catch (error) {
      console.error("AuthContext: Error signing out:", error);
    }
  };

  const value = {
    user,
    tenant,
    loading,
    isSystemAdmin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
